/**
 * ControlPanel API (`/ssml-dictionary`) クライアント (SBI-02)。
 *
 * SSML 辞書マスタ (SsmlDictionaryMaster) を CRUD する。読み仮名辞典 (`reading`) と
 * アクセント辞典 (`accent`) を言語別に管理する。**辞書の保守のみ**で、
 * 放送文への適用は UpdateContents 側の責務 (別 SBI)。
 *
 * - すべての呼び出しに `Authorization: Bearer <Cognito ID Token>` を付与する。
 * - エンドポイントは `AppConfig.apiUrl` をベースとする。
 * - 辞書は `(language_code, source_text)` の複合キーで一意なので、個別アイテムの
 *   パスは 2 階層 (`ssml-dictionary/{language_code}/{source_text}`) になる。
 *   `source_text` は日本語を含むため **必ず URL エンコード**して送る。
 *
 * ⚠️ `language_code` に渡す値は Polly の `LanguageCode` (`ja-JP`) ではなく
 *    **language_key** (`ja` / `en`) を使うこと。
 */
import type { AppConfig } from '../config';

import { ApiError, joinUrl } from './common';
import type {
  ApiErrorBody,
  SsmlDictionaryCreateRequest,
  SsmlDictionaryDeleteResponse,
  SsmlDictionaryListResponse,
  SsmlDictionarySaveResult,
  SsmlDictionaryType,
  SsmlDictionaryUpdateRequest,
} from './types';

export interface SsmlDictionaryClientOptions {
  /** Cognito ID トークンを取得する関数 (期限切れ時は AuthProvider 側で自動更新済み) */
  getIdToken: () => Promise<string>;
  /** AppConfig (apiUrl 取得用) */
  config: AppConfig;
  /** テスト時に差し込む fetch 実装 */
  fetchImpl?: typeof fetch;
}

export class SsmlDictionaryClient {
  private readonly options: SsmlDictionaryClientOptions;

  constructor(options: SsmlDictionaryClientOptions) {
    this.options = options;
  }

  /**
   * GET /ssml-dictionary (辞書一覧)
   *
   * 並びは言語 (日→英→中→韓) → 種別 → 変換元。
   *
   * @param dictionaryType 指定すると当該種別のみに絞り込む (省略時は全件)
   */
  async list(dictionaryType?: SsmlDictionaryType): Promise<SsmlDictionaryListResponse> {
    const query =
      dictionaryType !== undefined ? `?dictionary_type=${encodeURIComponent(dictionaryType)}` : '';
    return this.request<SsmlDictionaryListResponse>('GET', `ssml-dictionary${query}`);
  }

  /**
   * POST /ssml-dictionary (辞書を登録)
   *
   * 同一 `(language_code, source_text)` が既に存在する場合は **上書き** される
   * (種別が違っても重複扱い)。レスポンスの `warnings` は保存済みだが確認してほしい点。
   */
  async create(params: {
    languageCode: string;
    sourceText: string;
    dictionaryType: SsmlDictionaryType;
    replacement: string;
    note?: string;
    enabled?: boolean;
  }): Promise<SsmlDictionarySaveResult> {
    const payload: SsmlDictionaryCreateRequest = {
      language_code: params.languageCode,
      source_text: params.sourceText,
      dictionary_type: params.dictionaryType,
      replacement: params.replacement,
    };
    if (typeof params.note === 'string' && params.note !== '') {
      payload.note = params.note;
    }
    if (typeof params.enabled === 'boolean') {
      payload.enabled = params.enabled;
    }
    return this.request<SsmlDictionarySaveResult>('POST', 'ssml-dictionary', payload);
  }

  /**
   * PUT /ssml-dictionary/{language_code}/{source_text} (辞書を更新)
   *
   * 指定した項目のみ更新する (未指定は現在値を維持)。`note` に空文字を渡すと補足をクリアする。
   * `source_text` はキーなので変更できない。
   */
  async update(
    languageCode: string,
    sourceText: string,
    params: {
      dictionaryType?: SsmlDictionaryType;
      replacement?: string;
      note?: string;
      enabled?: boolean;
    },
  ): Promise<SsmlDictionarySaveResult> {
    const payload: SsmlDictionaryUpdateRequest = {};
    if (params.dictionaryType !== undefined) {
      payload.dictionary_type = params.dictionaryType;
    }
    if (typeof params.replacement === 'string') {
      payload.replacement = params.replacement;
    }
    if (typeof params.note === 'string') {
      payload.note = params.note;
    }
    if (typeof params.enabled === 'boolean') {
      payload.enabled = params.enabled;
    }
    return this.request<SsmlDictionarySaveResult>(
      'PUT',
      this.itemPath(languageCode, sourceText),
      payload,
    );
  }

  /** DELETE /ssml-dictionary/{language_code}/{source_text} (辞書を削除) */
  async remove(languageCode: string, sourceText: string): Promise<SsmlDictionaryDeleteResponse> {
    return this.request<SsmlDictionaryDeleteResponse>(
      'DELETE',
      this.itemPath(languageCode, sourceText),
    );
  }

  // ---------------------------------------------------------------------
  // 内部実装
  // ---------------------------------------------------------------------

  /** 個別アイテムのパス (2 階層のパスパラメータ) を組み立てる。 */
  private itemPath(languageCode: string, sourceText: string): string {
    return `ssml-dictionary/${encodeURIComponent(languageCode)}/${encodeURIComponent(sourceText)}`;
  }

  private async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const url = joinUrl(this.options.config.apiUrl, path);
    const idToken = await this.options.getIdToken();
    const fetchImpl = this.options.fetchImpl ?? fetch;

    const res = await fetchImpl(url, {
      method,
      headers: {
        ...(body !== undefined && { 'Content-Type': 'application/json' }),
        Authorization: `Bearer ${idToken}`,
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });

    if (!res.ok) {
      let errorBody: ApiErrorBody | undefined;
      try {
        errorBody = (await res.json()) as ApiErrorBody;
      } catch {
        // body が JSON で無い場合は無視
      }
      const message = errorBody?.message ?? `API request failed: HTTP ${res.status}`;
      throw new ApiError(res.status, message, errorBody);
    }

    return (await res.json()) as T;
  }
}

export { ApiError };
